import {
  Activity,
  AlertOctagon,
  Bell,
  BellRing,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleSlash2,
  Clock3,
  Eye,
  LockKeyhole,
  LogOut,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Unlock,
  X,
} from "lucide-react";
import { useDeferredValue, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiRequest, keys } from "./api";
import type { BlockedRedeemer, Overview, Pagination, SecurityEvent } from "./types";

type Tab = "incidents" | "blocked";

const dateTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

const relativeTime = (value: string) => {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
};

function SignIn() {
  const loginUrl = import.meta.env.VITE_VMS_LOGIN_URL?.trim();
  const href = loginUrl
    ? `${loginUrl}${loginUrl.includes("?") ? "&" : "?"}returnTo=${encodeURIComponent(window.location.href)}`
    : "#";
  return (
    <main className="auth-shell">
      <section className="auth-card">
        <div className="brand-mark"><ShieldAlert /></div>
        <p className="eyebrow">ADEX OPERATIONS</p>
        <h1>Voucher Security Center</h1>
        <p className="muted">
          Sign in with your Voucher Management account to review live incidents,
          blocked identities, and the redemption audit trail.
        </p>
        <a className={`primary-button wide ${loginUrl ? "" : "disabled"}`} href={href}>
          <LockKeyhole size={18} /> Sign in through VMS
        </a>
        {!loginUrl && <p className="error-text">VITE_VMS_LOGIN_URL is not configured.</p>}
      </section>
    </main>
  );
}

function AccessDenied() {
  const configuredAppUrl = import.meta.env.VITE_VMS_APP_URL?.trim();
  const loginUrl = import.meta.env.VITE_VMS_LOGIN_URL?.trim();
  let vmsUrl = configuredAppUrl || "#";
  if (!configuredAppUrl && loginUrl) {
    try {
      vmsUrl = new URL(loginUrl).origin;
    } catch {
      vmsUrl = "#";
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <div className="brand-mark"><LockKeyhole /></div>
        <p className="eyebrow">ACCESS RESTRICTED</p>
        <h1>Security role required</h1>
        <p className="muted">
          You are signed in, but this Voucher Management account is not authorized
          to review incidents or unblock identities.
        </p>
        <a className={`secondary-button wide ${vmsUrl === "#" ? "disabled" : ""}`} href={vmsUrl}>
          Return to Voucher Management
        </a>
      </section>
    </main>
  );
}

function MetricCard({ label, value, detail, tone, icon }: {
  label: string;
  value: number;
  detail: string;
  tone: string;
  icon: React.ReactNode;
}) {
  return (
    <article className={`metric-card ${tone}`}>
      <div className="metric-icon">{icon}</div>
      <div>
        <p>{label}</p>
        <strong>{value.toLocaleString()}</strong>
        <span>{detail}</span>
      </div>
    </article>
  );
}

function SeverityPill({ value }: { value: string }) {
  return <span className={`pill severity-${value}`}>{value}</span>;
}

function StatusPill({ value }: { value: string }) {
  return <span className={`pill status-${value}`}>{value}</span>;
}

function IncidentPanel({ event, close, mutate, busy }: {
  event: SecurityEvent;
  close: () => void;
  mutate: (payload: { id: number; action: string; note?: string }) => void;
  busy: boolean;
}) {
  const [note, setNote] = useState("");
  return (
    <div className="drawer-backdrop" onMouseDown={close}>
      <aside className="drawer" onMouseDown={(event) => event.stopPropagation()}>
        <button className="icon-button drawer-close" onClick={close} aria-label="Close"><X /></button>
        <p className="eyebrow">INCIDENT #{event.id}</p>
        <div className="pill-row"><SeverityPill value={event.severity} /><StatusPill value={event.status} /></div>
        <h2>{event.title}</h2>
        <p className="drawer-detail">{event.detail}</p>
        <dl className="detail-grid">
          <div><dt>Identity</dt><dd>{event.display_identifier}</dd></div>
          <div><dt>Canonical ID</dt><dd className="mono">{event.redeemer_id}</dd></div>
          <div><dt>Voucher ID</dt><dd>{event.voucher_id ?? "Unknown"}</dd></div>
          <div><dt>Occurrences</dt><dd>{event.occurrence_count}</dd></div>
          <div><dt>First detected</dt><dd>{dateTime(event.first_detected_at)}</dd></div>
          <div><dt>Last detected</dt><dd>{dateTime(event.last_detected_at)}</dd></div>
          <div className="span-two"><dt>Automatic action</dt><dd>{event.action_taken?.replaceAll("_", " ") ?? "None"}</dd></div>
        </dl>
        {event.status !== "resolved" && (
          <div className="resolution-box">
            <label htmlFor="resolution">Resolution note</label>
            <textarea id="resolution" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was investigated and resolved?" />
          </div>
        )}
        <div className="drawer-actions">
          {event.status === "open" && (
            <button className="secondary-button" disabled={busy} onClick={() => mutate({ id: event.id, action: "acknowledge" })}>
              <Eye size={17} /> Acknowledge
            </button>
          )}
          {event.status !== "resolved" ? (
            <button className="primary-button" disabled={busy || note.trim().length < 3} onClick={() => mutate({ id: event.id, action: "resolve", note })}>
              <CheckCircle2 size={17} /> Resolve incident
            </button>
          ) : (
            <button className="secondary-button" disabled={busy} onClick={() => mutate({ id: event.id, action: "reopen" })}>
              <RefreshCw size={17} /> Reopen
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

function Pager({ pagination, setPage }: { pagination?: Pagination; setPage: (page: number) => void }) {
  if (!pagination || pagination.totalPages <= 1) return null;
  return (
    <div className="pager">
      <span>{pagination.total.toLocaleString()} records</span>
      <button className="icon-button" disabled={pagination.page <= 1} onClick={() => setPage(pagination.page - 1)}><ChevronLeft /></button>
      <strong>{pagination.page} / {pagination.totalPages}</strong>
      <button className="icon-button" disabled={pagination.page >= pagination.totalPages} onClick={() => setPage(pagination.page + 1)}><ChevronRight /></button>
    </div>
  );
}

function Dashboard() {
  const client = useQueryClient();
  const [tab, setTab] = useState<Tab>("incidents");
  const [incidentPage, setIncidentPage] = useState(1);
  const [blockedPage, setBlockedPage] = useState(1);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [status, setStatus] = useState("active");
  const [severity, setSeverity] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(null);
  const [unblockTarget, setUnblockTarget] = useState<BlockedRedeemer | null>(null);
  const [unblockReason, setUnblockReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: keys.overview,
    queryFn: () => apiRequest<Overview>("/api/security/overview"),
    refetchInterval: 30_000,
  });
  const ntfy = useQuery({
    queryKey: keys.ntfy,
    queryFn: () => apiRequest<{
      enabled: boolean;
      subscribeUrl: string | null;
      topic: string | null;
    }>("/api/security/notifications/ntfy"),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const incidentFilters = useMemo(() => ({ page: incidentPage, search: deferredSearch, status, severity }), [incidentPage, deferredSearch, status, severity]);
  const incidents = useQuery({
    queryKey: keys.events(incidentFilters),
    queryFn: () => {
      const params = new URLSearchParams({ page: String(incidentPage), pageSize: "25", search: deferredSearch, severity });
      params.set("status", status);
      return apiRequest<{ events: SecurityEvent[]; pagination: Pagination }>(`/api/security/events?${params}`);
    },
    refetchInterval: 30_000,
  });
  const blockedFilters = useMemo(() => ({ page: blockedPage, search: deferredSearch }), [blockedPage, deferredSearch]);
  const blocked = useQuery({
    queryKey: keys.blocked(blockedFilters),
    queryFn: () => apiRequest<{ redeemers: BlockedRedeemer[]; pagination: Pagination }>(
      `/api/security/blocked-redeemers?page=${blockedPage}&pageSize=25&active=true&search=${encodeURIComponent(deferredSearch)}`
    ),
    enabled: tab === "blocked",
    refetchInterval: 30_000,
  });

  const invalidateSecurity = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["security", "overview"] }),
      client.invalidateQueries({ queryKey: ["security", "events"] }),
      client.invalidateQueries({ queryKey: ["security", "blocked"] }),
    ]);
  };
  const eventMutation = useMutation({
    mutationFn: ({ id, action, note }: { id: number; action: string; note?: string }) =>
      apiRequest(`/api/security/events/${id}`, { method: "PATCH", body: JSON.stringify({ action, note }) }),
    onSuccess: async () => { setSelectedEvent(null); setNotice("Incident updated."); await invalidateSecurity(); },
    onError: (error) => setNotice(error instanceof Error ? error.message : "Update failed."),
  });
  const unblockMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiRequest(`/api/security/blocked-redeemers/${id}/unblock`, { method: "POST", body: JSON.stringify({ reason }) }),
    onSuccess: async () => { setUnblockTarget(null); setUnblockReason(""); setNotice("Identity unblocked."); await invalidateSecurity(); },
    onError: (error) => setNotice(error instanceof Error ? error.message : "Unblock failed."),
  });

  const activeEvents = incidents.data?.events ?? [];
  const isRefreshing = overview.isFetching || incidents.isFetching || blocked.isFetching;
  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand"><div className="brand-mark small"><ShieldAlert /></div><div><strong>ADEX</strong><span>Voucher Security</span></div></div>
        <div className="top-actions">
          <span className="live-state"><i /> Live · 30s</span>
          {ntfy.data?.enabled && ntfy.data.subscribeUrl ? (
            <a
              className="secondary-button compact"
              href={ntfy.data.subscribeUrl}
              target="_blank"
              rel="noreferrer"
              title={`Subscribe to ${ntfy.data.topic ?? "the VMS security topic"} in ntfy`}
            >
              <Bell size={16} /> Open ntfy alerts
            </a>
          ) : (
            <button
              className="secondary-button compact"
              disabled
              title={ntfy.isError ? "ntfy configuration is unavailable" : "ntfy is not configured"}
            >
              <Bell size={16} /> ntfy unavailable
            </button>
          )}
          <button
            className="icon-button"
            onClick={async () => {
              await apiRequest("/api/auth/logout", { method: "POST" });
              client.clear();
              window.location.reload();
            }}
            aria-label="Sign out"
            title="Sign out"
          ><LogOut /></button>
          <button className="icon-button" disabled={isRefreshing} onClick={() => void invalidateSecurity()} aria-label="Refresh"><RefreshCw className={isRefreshing ? "spin" : ""} /></button>
        </div>
      </header>

      <main className="content">
        <section className="hero-row">
          <div><p className="eyebrow">SECURITY OPERATIONS</p><h1>Redemption threat monitor</h1><p className="muted">Atomic detection, automatic blocking, and a complete response trail.</p></div>
          <div className="system-health"><ShieldCheck /><div><strong>Protection active</strong><span>VMS enforcement is online</span></div></div>
        </section>

        <section className="metrics">
          <MetricCard label="Open incidents" value={overview.data?.openIncidents ?? 0} detail="Need operator review" tone="red" icon={<AlertOctagon />} />
          <MetricCard label="Blocked identities" value={overview.data?.blockedRedeemers ?? 0} detail="Enforced at redemption" tone="amber" icon={<CircleSlash2 />} />
          <MetricCard label="Critical · 24h" value={overview.data?.criticalLast24Hours ?? 0} detail="Repeat-use breaches" tone="violet" icon={<BellRing />} />
          <MetricCard label="Rejected · 24h" value={overview.data?.rejectedAttemptsLast24Hours ?? 0} detail="Stopped before wallet credit" tone="blue" icon={<Activity />} />
        </section>

        <section className="workspace">
          <div className="workspace-head">
            <div className="tabs">
              <button className={tab === "incidents" ? "active" : ""} onClick={() => { setTab("incidents"); setSearch(""); }}>Incidents</button>
              <button className={tab === "blocked" ? "active" : ""} onClick={() => { setTab("blocked"); setSearch(""); }}>Blocked identities</button>
            </div>
            <div className="search-box"><Search /><input value={search} onChange={(e) => { setSearch(e.target.value); setIncidentPage(1); setBlockedPage(1); }} placeholder={tab === "incidents" ? "Search identity or incident" : "Search blocked identity"} /></div>
          </div>

          {tab === "incidents" ? (
            <>
              <div className="filter-row">
                <select value={status} onChange={(e) => { setStatus(e.target.value); setIncidentPage(1); }}><option value="active">Active incidents</option><option value="open">Open</option><option value="acknowledged">Acknowledged</option><option value="resolved">Resolved</option><option value="all">All statuses</option></select>
                <select value={severity} onChange={(e) => { setSeverity(e.target.value); setIncidentPage(1); }}><option value="all">All severities</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select>
              </div>
              <div className="table-wrap">
                <table className="incident-table"><thead><tr><th>Incident</th><th>Identity</th><th>Severity</th><th>Status</th><th>Hits</th><th>Last detected</th><th /></tr></thead>
                  <tbody>
                    {activeEvents.map((event) => (
                      <tr key={event.id}><td><strong>{event.title}</strong><span>{event.event_type.replaceAll("_", " ")}</span></td><td><strong className="mono">{event.display_identifier}</strong><span>{event.redeemer_type}</span></td><td><SeverityPill value={event.severity} /></td><td><StatusPill value={event.status} /></td><td><span className="hit-count">{event.occurrence_count}</span></td><td><strong>{relativeTime(event.last_detected_at)}</strong><span>{dateTime(event.last_detected_at)}</span></td><td><button className="table-action" onClick={() => setSelectedEvent(event)}>Review <ChevronRight /></button></td></tr>
                    ))}
                  </tbody>
                </table>
                {incidents.isPending && <div className="empty-state"><RefreshCw className="spin" /> Loading incidents…</div>}
                {!incidents.isPending && activeEvents.length === 0 && <div className="empty-state"><ShieldCheck /> No incidents match these filters.</div>}
                {incidents.isError && <div className="empty-state error-text">{incidents.error.message}</div>}
              </div>
              <Pager pagination={incidents.data?.pagination} setPage={setIncidentPage} />
            </>
          ) : (
            <>
              <div className="table-wrap">
                <table className="blocked-table"><thead><tr><th>Identity</th><th>Type</th><th>Reason</th><th>Blocked</th><th>Source</th><th /></tr></thead>
                  <tbody>
                    {(blocked.data?.redeemers ?? []).map((entry) => (
                      <tr key={entry.id}><td><strong className="mono">{entry.display_identifier}</strong><span>{entry.redeemer_id}</span></td><td>{entry.redeemer_type}</td><td className="wide-cell">{entry.reason}</td><td><strong>{relativeTime(entry.blocked_at)}</strong><span>{dateTime(entry.blocked_at)}</span></td><td>{entry.blocked_by}</td><td><button className="table-action safe" onClick={() => setUnblockTarget(entry)}><Unlock /> Unblock</button></td></tr>
                    ))}
                  </tbody>
                </table>
                {blocked.isPending && <div className="empty-state"><RefreshCw className="spin" /> Loading blocked identities…</div>}
                {!blocked.isPending && (blocked.data?.redeemers.length ?? 0) === 0 && <div className="empty-state"><ShieldCheck /> No blocked identities match this search.</div>}
              </div>
              <Pager pagination={blocked.data?.pagination} setPage={setBlockedPage} />
            </>
          )}
        </section>
      </main>

      {selectedEvent && <IncidentPanel event={selectedEvent} close={() => setSelectedEvent(null)} mutate={eventMutation.mutate} busy={eventMutation.isPending} />}
      {unblockTarget && (
        <div className="modal-backdrop" onMouseDown={() => setUnblockTarget(null)}><section className="modal" onMouseDown={(e) => e.stopPropagation()}><button className="icon-button modal-close" onClick={() => setUnblockTarget(null)}><X /></button><div className="modal-icon"><Unlock /></div><h2>Unblock this identity?</h2><p className="muted mono">{unblockTarget.display_identifier}</p><p>Future voucher redemptions will be allowed immediately. The original block and incident logs remain preserved.</p><label htmlFor="unblock-reason">Required reason</label><textarea id="unblock-reason" value={unblockReason} onChange={(e) => setUnblockReason(e.target.value)} placeholder="Reason and operator context" /><div className="modal-actions"><button className="secondary-button" onClick={() => setUnblockTarget(null)}>Cancel</button><button className="primary-button safe" disabled={unblockReason.trim().length < 3 || unblockMutation.isPending} onClick={() => unblockMutation.mutate({ id: unblockTarget.id, reason: unblockReason })}><Unlock size={17} /> Confirm unblock</button></div></section></div>
      )}
      {notice && <button className="toast" onClick={() => setNotice(null)}><span>{notice}</span><X /></button>}
    </div>
  );
}

export default function App() {
  const session = useQuery({
    queryKey: keys.session,
    queryFn: async () => {
      try {
        await apiRequest("/api/security/session");
        return "authorized" as const;
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) return "forbidden" as const;
        if (error instanceof ApiError && (error.status === 400 || error.status === 401)) {
          return "unauthenticated" as const;
        }
        throw error;
      }
    },
    retry: false,
  });
  if (session.isPending) return <main className="loading-screen"><ShieldAlert /><RefreshCw className="spin" /><span>Establishing secure session…</span></main>;
  if (session.data === "forbidden") return <AccessDenied />;
  if (session.data !== "authorized") return <SignIn />;
  return <Dashboard />;
}
